// Message body editor: a contenteditable surface whose hidden field
// holds the body in the format its channel stores.
//
// A formatted body (email) is the markup internal/richtext renders:
// **bold**, *italic*, [label](address), "- " and "1. " list lines, and a
// backslash before a literal *, [, ], or \. Every other character is
// literal and every line break stays. A plain body (a text message) is
// the text as typed. The server renders the surface's starting DOM the
// way this file reads it back, one <div> per line and <div><br></div>
// for an empty one, so an untouched body round-trips.
//
// The formats come from data-formats. With data-channel-field, the
// form's channel select swaps them from data-formats-by-channel:
// switching to text strips the formatting from view, and switching back
// before editing restores it.
//
// Targets: [data-message-editor].

import { safeLinkAddress } from "./link_address.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const BLOCK_TAGS = new Set([
  "DIV",
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "PRE",
  "TABLE",
  "TR",
]);
const LIST_TAGS = new Set(["UL", "OL"]);

const IS_MAC = /Mac|iPhone|iPad/.test(
  navigator.platform || navigator.userAgent,
);

const COMMANDS = {
  bold: "bold",
  italic: "italic",
  bullet_list: "insertUnorderedList",
  numbered_list: "insertOrderedList",
};

// SHORTCUTS maps a key pressed with Cmd or Ctrl to its format. Underline
// is listed only so the browser's own underline, a format no channel
// carries, is swallowed.
const SHORTCUTS = { b: "bold", i: "italic", k: "link", u: null };

export function initMessageEditor(root = document) {
  root.querySelectorAll("[data-message-editor]").forEach((wrapper) => {
    if (!firstBind(wrapper)) return;
    wireEditor(wrapper);
  });
}

function wireEditor(wrapper) {
  const surface = wrapper.querySelector("[data-message-surface]");
  const input = wrapper.querySelector("[data-message-input]");
  const toolbar = wrapper.querySelector("[data-message-toolbar]");
  const label = wrapper.querySelector("[data-message-label]");
  if (!surface || !input) return;

  let formats = parseFormats(wrapper.dataset.formats);
  // What the formatted body looked like when the agent switched to a
  // plain channel, so switching straight back loses nothing.
  let stash = null;

  const sync = () => {
    const next = formats.size > 0 ? toMarkup(surface) : toPlain(surface);
    surface.toggleAttribute("data-empty", toPlain(surface).trim() === "");
    if (next === input.value) return;

    input.value = next;
    input.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const link = toolbar ? wireLink(wrapper, surface, sync) : null;

  const setFormats = (next) => {
    const wasFormatted = formats.size > 0;
    formats = next;
    const isFormatted = formats.size > 0;

    let rebuilt = false;
    if (wasFormatted && !isFormatted) {
      link?.close(false);
      stash = { html: surface.innerHTML, plain: toPlain(surface) };
      surface.innerHTML = plainLinesHTML(stash.plain);
      rebuilt = true;
    } else if (!wasFormatted && isFormatted && stash) {
      if (toPlain(surface) === stash.plain) {
        surface.innerHTML = stash.html;
        rebuilt = true;
      }
      stash = null;
    }
    // Replacing the DOM drops a focused caret to the start.
    if (rebuilt && document.activeElement === surface) {
      restoreRange(endOfSurface(surface));
    }

    renderToolbar();
    sync();
  };

  const buttons = () =>
    toolbar
      ? Array.from(toolbar.querySelectorAll("[data-message-format]")).filter(
          (button) => !button.closest("[hidden]"),
        )
      : [];

  const renderToolbar = () => {
    if (!toolbar) return;

    toolbar.hidden = formats.size === 0;
    toolbar.querySelectorAll("[data-message-format-group]").forEach((group) => {
      const shown = group.dataset.messageFormatGroup
        .split(" ")
        .some((format) => formats.has(format));
      group.hidden = !shown;
    });
    toolbar.querySelectorAll("[data-message-format]").forEach((button) => {
      button.hidden = !formats.has(button.dataset.messageFormat);
    });
    // Roving tabindex: the toolbar is one tab stop.
    buttons().forEach((button, i) => {
      button.tabIndex = i === 0 ? 0 : -1;
    });
  };

  const refreshPressed = () => {
    if (!toolbar || formats.size === 0) return;

    const selection = window.getSelection();
    const inside =
      selection &&
      selection.rangeCount > 0 &&
      surface.contains(selection.anchorNode);
    toolbar.querySelectorAll("[data-message-format]").forEach((button) => {
      const active =
        inside && isActive(button.dataset.messageFormat, selection, surface);
      button.setAttribute("aria-pressed", String(Boolean(active)));
    });
  };

  surface.addEventListener("input", sync);
  surface.addEventListener("blur", sync);
  const onSelectionChange = () => {
    // A swapped-out editor drops its listener the next time it fires.
    if (!surface.isConnected) {
      document.removeEventListener("selectionchange", onSelectionChange);
      return;
    }
    refreshPressed();
  };
  document.addEventListener("selectionchange", onSelectionChange);

  surface.addEventListener("keydown", (event) => {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey)
      return;

    const key = event.key.toLowerCase();
    if (!(key in SHORTCUTS)) return;

    const format = SHORTCUTS[key];
    if (format === "link") {
      event.preventDefault();
      if (formats.has("link")) link?.open();
      return;
    }
    if (!format || !formats.has(format)) event.preventDefault();
  });

  surface.addEventListener("paste", (event) => {
    event.preventDefault();
    const data = event.clipboardData;
    if (!data) return;

    const html = data.getData("text/html");
    if (formats.size > 0 && html) {
      insertFragment(surface, cleanFragment(html, formats));
    } else {
      // insertText keeps the paste on the undo stack.
      document.execCommand("insertText", false, data.getData("text/plain"));
    }
    sync();
  });

  label?.addEventListener("click", () => surface.focus());

  // A script that fills the body, such as a streamed AI draft, sets plain
  // text through this event rather than reaching into the DOM.
  surface.addEventListener("message-editor:set-text", (event) => {
    surface.innerHTML = plainLinesHTML(event.detail?.text || "");
    sync();
  });
  // A saved template arrives as the server renders an editor's starting
  // DOM (richtext.EditorHTML), which escapes every character it was
  // given, so its formatting survives the load.
  surface.addEventListener("message-editor:set-html", (event) => {
    surface.innerHTML = event.detail?.html || "";
    sync();
  });
  wrapper
    .querySelector("[data-message-editor-clear]")
    ?.addEventListener("click", () => {
      surface.innerHTML = "";
      sync();
      surface.focus();
    });

  if (toolbar) {
    toolbar.querySelectorAll("[data-message-format]").forEach((button) => {
      const format = button.dataset.messageFormat;
      const key = { bold: "B", italic: "I", link: "K" }[format];
      if (key) {
        const shortcut = IS_MAC ? `⌘${key}` : `Ctrl+${key}`;
        button.title = `${button.getAttribute("aria-label")} (${shortcut})`;
        button.setAttribute("aria-keyshortcuts", `Control+${key} Meta+${key}`);
      }
      if (format === "link") return;

      button.addEventListener("mousedown", (event) => event.preventDefault());
      button.addEventListener("click", () => {
        surface.focus();
        document.execCommand(COMMANDS[format], false, null);
        sync();
        refreshPressed();
      });
    });

    toolbar.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
        return;
      const list = buttons();
      const current = list.indexOf(document.activeElement);
      if (current < 0) return;

      event.preventDefault();
      let next = current;
      if (event.key === "ArrowLeft")
        next = (current - 1 + list.length) % list.length;
      if (event.key === "ArrowRight") next = (current + 1) % list.length;
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = list.length - 1;
      list.forEach((button, i) => {
        button.tabIndex = i === next ? 0 : -1;
      });
      list[next].focus();
    });
  }

  const channelName = wrapper.dataset.channelField;
  const form = wrapper.closest("form");
  if (channelName && form) {
    let byChannel = {};
    try {
      byChannel = JSON.parse(wrapper.dataset.formatsByChannel || "{}");
    } catch (_) {
      byChannel = {};
    }
    const channel = form.elements.namedItem(channelName);
    channel?.addEventListener("change", () => {
      setFormats(new Set(byChannel[channel.value] || []));
    });
  }

  renderToolbar();
  sync();
}

function parseFormats(raw) {
  return new Set((raw || "").split(" ").filter(Boolean));
}

// isActive reports whether the caret sits inside a format. It looks no
// further out than the surface: an editor inside a page's own list would
// otherwise read as being in one.
function isActive(format, selection, surface) {
  const selector = {
    bold: "b, strong",
    italic: "i, em",
    link: "a",
    bullet_list: "ul",
    numbered_list: "ol",
  }[format];
  if (!selector) return false;

  const found = closestElement(selection.anchorNode, selector);

  return Boolean(found && surface.contains(found));
}

function closestElement(node, selector) {
  const element =
    node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;

  return element ? element.closest(selector) : null;
}

// wireLink runs the link popover: open from the button or Cmd/Ctrl+K,
// add or update the link on the selection, remove it, and close on
// Escape or a click elsewhere with the selection put back.
function wireLink(wrapper, surface, sync) {
  const button = wrapper.querySelector('[data-message-format="link"]');
  const popover = wrapper.querySelector("[data-message-link-popover]");
  const field = wrapper.querySelector("[data-message-link-input]");
  const apply = wrapper.querySelector("[data-message-link-apply]");
  const remove = wrapper.querySelector("[data-message-link-remove]");
  const cancel = wrapper.querySelector("[data-message-link-cancel]");
  const error = wrapper.querySelector("[data-message-link-error]");
  if (!button || !popover || !field || !apply) return null;

  let savedRange = null;
  let editing = null;
  if (popover.id) button.setAttribute("aria-controls", popover.id);

  const setError = (shown) => {
    if (error) error.hidden = !shown;
    // The aria-invalid: style needs the value "true"; a bare attribute
    // shows nothing.
    if (shown) field.setAttribute("aria-invalid", "true");
    else field.removeAttribute("aria-invalid");
  };

  const close = (restore) => {
    if (popover.hidden) return;

    popover.hidden = true;
    button.setAttribute("aria-expanded", "false");
    field.value = "";
    setError(false);
    if (restore) {
      surface.focus();
      restoreRange(savedRange);
    }
    savedRange = null;
    editing = null;
  };

  const open = () => {
    if (!popover.hidden) {
      close(true);
      return;
    }

    const selection = window.getSelection();
    savedRange =
      selection &&
      selection.rangeCount > 0 &&
      surface.contains(selection.anchorNode)
        ? selection.getRangeAt(0).cloneRange()
        : null;
    const found = savedRange && closestElement(savedRange.startContainer, "a");
    editing = found && surface.contains(found) ? found : null;

    field.value = editing ? editing.getAttribute("href") : "";
    apply.textContent = editing ? "Update" : "Add link";
    if (remove) remove.hidden = !editing;
    popover.hidden = false;
    button.setAttribute("aria-expanded", "true");
    field.focus();
    field.select();
  };

  const commit = () => {
    const href = safeLinkAddress(field.value, { email: true });
    if (!href) {
      setError(true);
      field.focus();
      return;
    }

    surface.focus();
    if (editing) {
      editing.setAttribute("href", href);
    } else {
      restoreRange(savedRange);
      const selection = window.getSelection();
      if (!selection.rangeCount || selection.isCollapsed) {
        // No text selected: the address is its own label.
        const text = document.createTextNode(href.replace(/^mailto:/, ""));
        const range = selection.rangeCount
          ? selection.getRangeAt(0)
          : endOfSurface(surface);
        range.insertNode(text);
        range.selectNodeContents(text);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.execCommand("createLink", false, href);
      selection.collapseToEnd();
    }
    sync();
    close(false);
  };

  button.addEventListener("mousedown", (event) => event.preventDefault());
  button.addEventListener("click", open);
  apply.addEventListener("mousedown", (event) => event.preventDefault());
  apply.addEventListener("click", commit);
  remove?.addEventListener("click", () => {
    if (editing) {
      const range = document.createRange();
      range.selectNodeContents(editing);
      surface.focus();
      restoreRange(range);
      document.execCommand("unlink", false, null);
      sync();
    }
    close(false);
  });
  cancel?.addEventListener("click", () => close(true));
  field.addEventListener("input", () => setError(false));
  field.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    }
  });
  wrapper.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !popover.hidden) {
      event.preventDefault();
      close(true);
    }
  });
  const onPointerDown = (event) => {
    if (!wrapper.isConnected) {
      document.removeEventListener("pointerdown", onPointerDown);
      return;
    }
    if (
      !popover.hidden &&
      !popover.contains(event.target) &&
      event.target !== button &&
      !button.contains(event.target)
    ) {
      close(false);
    }
  };
  document.addEventListener("pointerdown", onPointerDown);

  return { open, close };
}

function restoreRange(range) {
  if (!range) return;

  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
}

function endOfSurface(surface) {
  const range = document.createRange();
  range.selectNodeContents(surface.lastElementChild || surface);
  range.collapse(false);

  return range;
}

// cleanFragment keeps what the formats allow from pasted HTML. An element
// outside them gives up its tag but keeps its text, and the bold or
// italic a word processor writes as a style becomes the tag it means.
function cleanFragment(html, formats) {
  const doc = new DOMParser().parseFromString(
    `<div>${html}</div>`,
    "text/html",
  );
  const root = doc.body.firstElementChild;
  const allowed = new Set(["DIV", "P", "BR"]);
  if (formats.has("bold")) ["B", "STRONG"].forEach((tag) => allowed.add(tag));
  if (formats.has("italic")) ["I", "EM"].forEach((tag) => allowed.add(tag));
  if (formats.has("link")) allowed.add("A");
  if (formats.has("bullet_list"))
    ["UL", "LI"].forEach((tag) => allowed.add(tag));
  if (formats.has("numbered_list"))
    ["OL", "LI"].forEach((tag) => allowed.add(tag));

  const styled = (node) => {
    const style = node.getAttribute("style") || "";
    const weight = style.match(/font-weight:\s*(\w+)/i)?.[1];

    return {
      bold: weight === "bold" || Number(weight) >= 600,
      notBold:
        weight === "normal" || (Number(weight) > 0 && Number(weight) < 600),
      italic: /font-style:\s*italic/i.test(style),
    };
  };

  const clean = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove();
      } else if (child.nodeType === Node.TEXT_NODE) {
        // Source formatting is not content: a newline or tab in pasted
        // HTML renders as a space.
        child.nodeValue = child.nodeValue.replace(/ *[\t\n\r]+ */g, " ");
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        clean(child);
      }
    });
    if (node === root || node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName;
    if (["SCRIPT", "STYLE", "TEMPLATE", "META", "TITLE"].includes(tag)) {
      node.remove();
      return;
    }

    const look = styled(node);
    // Google Docs wraps a whole paste in <b style="font-weight:normal">.
    if ((tag === "B" || tag === "STRONG") && look.notBold) {
      node.replaceWith(...node.childNodes);
      return;
    }
    const href = tag === "A" ? safeLinkAddress(node.getAttribute("href")) : "";
    if (!allowed.has(tag) || (tag === "A" && !href)) {
      let replacement = Array.from(node.childNodes);
      if (tag === "SPAN" && look.italic && formats.has("italic")) {
        const em = doc.createElement("em");
        em.append(...replacement);
        replacement = [em];
      }
      if (tag === "SPAN" && look.bold && formats.has("bold")) {
        const strong = doc.createElement("strong");
        strong.append(...replacement);
        replacement = [strong];
      }
      node.replaceWith(...replacement);
      return;
    }
    Array.from(node.attributes).forEach((attr) =>
      node.removeAttribute(attr.name),
    );
    if (href) node.setAttribute("href", href);
  };
  clean(root);

  // Whitespace between blocks is layout, not text.
  Array.from(root.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE && child.nodeValue.trim() === "") {
      child.remove();
    }
  });

  const fragment = document.createDocumentFragment();
  fragment.append(...root.childNodes);

  return fragment;
}

function insertFragment(surface, fragment) {
  const selection = window.getSelection();
  if (
    !selection ||
    selection.rangeCount === 0 ||
    !surface.contains(selection.anchorNode)
  ) {
    surface.append(fragment);
    return;
  }

  const range = selection.getRangeAt(0);
  range.deleteContents();
  const last = fragment.lastChild;
  range.insertNode(fragment);
  if (last) {
    range.setStartAfter(last);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

function plainLinesHTML(text) {
  if (!text) return "";

  const holder = document.createElement("div");
  text.split(/\r?\n/).forEach((line) => {
    const div = document.createElement("div");
    if (line) {
      div.textContent = line;
    } else {
      div.append(document.createElement("br"));
    }
    holder.append(div);
  });

  return holder.innerHTML;
}

// Reading the surface back. Both formats walk the DOM into lines; the
// markup reader escapes what the agent typed and marks what they
// formatted, the plain reader keeps only the words.

export function toMarkup(surface) {
  return readLines(surface, true).join("\n").replace(/\n+$/, "");
}

export function toPlain(surface) {
  return readLines(surface, false).join("\n").replace(/\n+$/, "");
}

function hasBlock(node) {
  return Array.from(node.children).some(
    (child) => BLOCK_TAGS.has(child.tagName) || LIST_TAGS.has(child.tagName),
  );
}

function readLines(surface, markup) {
  const lines = [];
  let current = "";

  const push = (line) => lines.push(line);
  const flush = () => {
    if (current !== "") push(markup ? escapeLineStart(current) : current);
    current = "";
  };

  const readList = (list) => {
    let number = list.hasAttribute("start")
      ? Number(list.getAttribute("start")) || 0
      : 1;
    list.querySelectorAll(":scope > li").forEach((item) => {
      // A number past 999 cannot open a list line, so it reads as text.
      const marker =
        list.tagName === "UL"
          ? "- "
          : number > 999
            ? `${number++}\\. `
            : `${number++}. `;
      const nested = Array.from(item.children).filter((child) =>
        LIST_TAGS.has(child.tagName),
      );
      const text = readInline(item, markup, nested);
      // A list line cannot hold a break, so one inside an item reads as a
      // space; a list inside it becomes items of its own.
      push(marker + text.replace(/\s*\n\s*/g, " ").trim());
      nested.forEach(readList);
    });
  };

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += readText(node.nodeValue, markup);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName;
    if (tag === "BR") {
      push(markup ? escapeLineStart(current) : current);
      current = "";
      return;
    }
    if (LIST_TAGS.has(tag)) {
      flush();
      readList(node);
      return;
    }
    // An inline element wrapped around blocks, as a word processor's
    // clipboard writes, reads as its blocks.
    if (BLOCK_TAGS.has(tag) || hasBlock(node)) {
      flush();
      node.childNodes.forEach(walk);
      flush();
      return;
    }

    current += readInlineElement(node, markup);
  };

  surface.childNodes.forEach(walk);
  flush();

  return lines;
}

// FORMAT_GROUP names the tags that mark the same format, so adjacent
// runs of one format read as one: "*a**b*" would parse as one italic
// holding a literal "**".
const FORMAT_GROUP = { B: "bold", STRONG: "bold", I: "italic", EM: "italic" };

// readInline reads an element's inline content, skipping the nodes in
// skip.
function readInline(node, markup, skip = []) {
  let out = "";
  const children = Array.from(node.childNodes).filter((c) => !skip.includes(c));
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === Node.TEXT_NODE) {
      out += readText(child.nodeValue, markup);
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    if (child.tagName === "BR") {
      out += " ";
      continue;
    }

    const group = FORMAT_GROUP[child.tagName];
    if (markup && group) {
      const run = [child];
      while (
        i + 1 < children.length &&
        children[i + 1].nodeType === Node.ELEMENT_NODE &&
        FORMAT_GROUP[children[i + 1].tagName] === group
      ) {
        run.push(children[++i]);
      }
      const inner = run.map((el) => readInline(el, true)).join("");
      out += wrap(inner, group === "bold" ? "**" : "*");
      continue;
    }
    out += readInlineElement(child, markup);
  }

  return out;
}

function readInlineElement(node, markup) {
  if (!markup) {
    if (node.tagName !== "A") return readInline(node, false);

    const href = node.getAttribute("href") || "";
    const address = href.replace(/^mailto:/, "");
    const label = node.textContent;

    return label && label !== address && label !== href
      ? `${label} (${address})`
      : address || label;
  }

  const inner = readInline(node, true);
  switch (node.tagName) {
    case "B":
    case "STRONG":
      return wrap(inner, "**");
    case "I":
    case "EM":
      return wrap(inner, "*");
    case "A": {
      const href = safeLinkAddress(node.getAttribute("href"), { email: true });
      if (!href || !inner.trim()) return inner;
      // An address that is its own label reads back as the bare address
      // it was typed as.
      if (/^https?:/i.test(href) && node.textContent === href) return href;
      return `[${inner}](${href.replace(/[()\\]/g, encodeURIComponent)})`;
    }
    default:
      return inner;
  }
}

// wrap puts markers around text, outside any edge whitespace: the
// renderer only closes a marker that touches text.
function wrap(text, marker) {
  const match = text.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!match || match[2] === "") return text;

  return `${match[1]}${marker}${match[2]}${marker}${match[3]}`;
}

// readText turns the no-break spaces a contenteditable types for runs of
// spaces back into spaces: one is outside the GSM-7 alphabet, and would
// cost a text message its cheaper encoding.
function readText(text, markup) {
  const spaced = text.replace(/\u00a0/g, " ");

  return markup ? spaced.replace(/[\\*[\]]/g, "\\$&") : spaced;
}

// escapeLineStart stops a typed line from reading as a list item.
function escapeLineStart(line) {
  return line.replace(/^-( )/, "\\-$1").replace(/^(\d{1,3})\.( )/, "$1\\.$2");
}
